import type { Approval, ApprovalOption, Message, ThreadItem, ToolCall } from 'jcode-ui-core';
import { groupTimeline } from './grouping';
import { terminalStatusSeq } from './eventModel';
import { i18n } from '../i18n';
import type {
  GroupedTimelineItem,
  PermissionCardItem,
  RunViewEvent,
} from './types';

export interface PermissionPresentationState {
  decided?: Record<string, string>;
  disabled?: boolean;
}

export function toThreadItems(
  events: RunViewEvent[],
  permissionState: PermissionPresentationState = {},
): ThreadItem[] {
  const finalStatusSeq = terminalStatusSeq(events);
  return groupTimeline(events).map((item) => toThreadItem(item, finalStatusSeq, permissionState));
}

function toThreadItem(
  item: GroupedTimelineItem,
  finalStatusSeq: number | undefined,
  permissionState: PermissionPresentationState,
): ThreadItem {
  const timestamp = parseTimestamp(item.ts);

  switch (item.kind) {
    case 'text_block':
      return message(item.seq, 'assistant', item.text, timestamp);

    case 'user_message':
      // `author` is the Cloud attribution extension carried by the pinned
      // shared-conversation package. The intersection keeps this adapter
      // source-compatible with newer core packages that temporarily omit it.
      const userMessage: Message & { author?: string } = {
        id: `user-${item.seq}`,
        role: 'user',
        content: item.prompt,
        timestamp,
        source: item.by || undefined,
        author: item.by || undefined,
      };
      return {
        kind: 'message',
        seq: item.seq,
        data: userMessage,
      };

    case 'tool_card':
      return tool(item.seq, {
        id: `tool-${item.callId}`,
        toolCallID: item.callId,
        name: item.tool,
        args: item.args,
        output: item.output,
        displayOutput: item.output,
        error: item.isError ? item.output || 'Tool failed' : undefined,
        status:
          item.status === 'running'
            ? 'running'
            : item.status === 'failed'
              ? 'error'
              : 'done',
        timestamp,
      });

    case 'tool_call':
      return tool(item.seq, {
        id: `tool-call-${item.seq}`,
        toolCallID: item.callId,
        name: item.tool,
        args: item.args,
        status: 'running',
        timestamp,
      });

    case 'tool_result':
      return tool(item.seq, {
        id: `tool-result-${item.seq}`,
        toolCallID: item.callId,
        name: item.tool || 'tool',
        args: '{}',
        output: item.output,
        displayOutput: item.output,
        error: item.isError ? item.output || 'Tool failed' : undefined,
        status: item.isError ? 'error' : 'done',
        timestamp,
      });

    case 'permission_card': {
      const chosen = item.options.find((option) => option.optionId === item.resolvedOptionId);
      const approval: Approval = {
        id: item.requestId,
        tool_name: item.title,
        tool_args: item.toolArgs ?? i18n.t('run.permissionArgumentsUnavailable'),
        is_external: false,
        resolved: item.status === 'resolved',
        approved: chosen?.kind.startsWith('allow') ?? false,
        options: item.options.map(toApprovalOption),
        resolvedOptionId: item.resolvedOptionId,
        resolving: permissionState.disabled || !!permissionState.decided?.[item.requestId],
      };
      return { kind: 'approval', seq: item.seq, data: approval };
    }

    case 'status':
      return systemMessage(
        item.seq,
        item.seq === finalStatusSeq
          ? `Final status: ${formatStatus(item.status)} — end of run`
          : `Status: ${formatStatus(item.status)}`,
        timestamp,
      );

    case 'failure':
      return systemMessage(item.seq, item.message, timestamp, {
        level: 'error',
        detail: item.reason,
      });

    case 'artifact':
      return systemMessage(item.seq, `Artifact ready: ${item.artifact}`, timestamp);

    case 'git':
      return systemMessage(
        item.seq,
        item.commitSha
          ? `Pushed branch \`${item.branch}\` at \`${item.commitSha}\``
          : `Pushed branch \`${item.branch}\``,
        timestamp,
      );

    case 'result':
      return systemMessage(item.seq, `Result: ${item.message}`, timestamp);

    case 'permission_resolved':
      return systemMessage(
        item.seq,
        `Permission ${item.resolution === 'timeout' ? 'timed out' : 'resolved'}${
          item.optionId ? ` — ${item.optionId}` : ''
        }`,
        timestamp,
      );

    case 'session_info':
    case 'session_finish':
      return systemMessage(item.seq, item.message, timestamp);

    case 'unknown':
      return systemMessage(
        item.seq,
        `Unknown event: ${item.type}\n\n\`\`\`json\n${item.raw}\n\`\`\``,
        timestamp,
      );
  }
}

function toApprovalOption(option: PermissionCardItem['options'][number]): ApprovalOption {
  let kind: ApprovalOption['kind'] = 'custom';
  if (option.kind === 'allow_once') kind = 'allow_once';
  else if (option.kind === 'allow_always') kind = 'allow_always';
  else if (option.kind.startsWith('reject') || option.kind === 'deny') kind = 'deny';
  return { id: option.optionId, label: option.name, kind };
}

function message(
  seq: number,
  role: 'assistant',
  content: string,
  timestamp: number,
): ThreadItem {
  return {
    kind: 'message',
    seq,
    data: { id: `${role}-${seq}`, role, content, timestamp },
  };
}

function systemMessage(
  seq: number,
  content: string,
  timestamp: number,
  extra: { level?: 'error' | 'notice'; detail?: string } = {},
): ThreadItem {
  return {
    kind: 'message',
    seq,
    data: {
      id: `system-${seq}`,
      role: 'system',
      content,
      timestamp,
      level: 'notice',
      ...extra,
    },
  };
}

function tool(seq: number, data: ToolCall): ThreadItem {
  return { kind: 'tool', seq, data };
}

function parseTimestamp(ts: string): number {
  const parsed = Date.parse(ts);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatStatus(status: string): string {
  if (!status) return 'Unknown';
  const normalized = status.replaceAll('_', ' ');
  return normalized[0]!.toUpperCase() + normalized.slice(1);
}
